import type { CSSProperties } from 'vue'
import type {
  ButtonTargetId,
  ButtonStyleVariant,
  DropShadowConfig,
  ScreenBrandColors,
  StyledButtonConfig,
} from '@/types/form.types'

export type ButtonStyleFallbacks = {
  backgroundColor: string
  textColor: string
  borderColor: string
}

const DEFAULT_VARIANT: ButtonStyleVariant = 'solid'
const DEFAULT_TARGET: ButtonTargetId = 'prize-primary-button'

const DEFAULT_DROP_SHADOW: DropShadowConfig = {
  enabled: false,
  offsetX: 0,
  offsetY: 4,
  blur: 12,
  spread: 0,
  color: 'rgba(0,0,0,0.35)',
}

export const DEFAULT_BUTTON_CONFIGS: Record<'registration' | 'interaction' | 'prize', StyledButtonConfig> = {
  registration: {
    attributeId: 'registration-primary-button',
    label: 'Submit / Login',
    target: '_blank',
    styleVariant: 'solid',
    borderRadius: 22,
    blurPx: 12,
    dropShadow: { ...DEFAULT_DROP_SHADOW },
  },
  interaction: {
    attributeId: 'interaction-primary-button',
    label: 'Open Camera',
    target: '_blank',
    styleVariant: 'solid',
    borderRadius: 22,
    blurPx: 12,
    dropShadow: { ...DEFAULT_DROP_SHADOW },
  },
  prize: {
    attributeId: 'prize-primary-button',
    label: 'Redeem Here',
    target: '_blank',
    styleVariant: 'solid',
    borderRadius: 22,
    blurPx: 12,
    dropShadow: { ...DEFAULT_DROP_SHADOW },
  },
}

export const clampButtonRadius = (value?: number): number => {
  if (!Number.isFinite(value)) {
    return 22
  }
  return Math.min(50, Math.max(0, Math.round(value as number)))
}

export const clampBlur = (value?: number): number => {
  if (!Number.isFinite(value)) {
    return 12
  }
  return Math.min(24, Math.max(0, Math.round(value as number)))
}

export const clampShadowOffset = (value?: number): number => {
  if (!Number.isFinite(value)) return 0
  return Math.min(24, Math.max(-24, Math.round(value as number)))
}

export const clampShadowBlur = (value?: number): number => {
  if (!Number.isFinite(value)) return 12
  return Math.min(48, Math.max(0, Math.round(value as number)))
}

export const clampShadowSpread = (value?: number): number => {
  if (!Number.isFinite(value)) return 0
  return Math.min(24, Math.max(-12, Math.round(value as number)))
}

export const normalizeDropShadow = (
  value?: Partial<DropShadowConfig> | null,
): DropShadowConfig => ({
  enabled: value?.enabled ?? DEFAULT_DROP_SHADOW.enabled,
  offsetX: clampShadowOffset(value?.offsetX ?? DEFAULT_DROP_SHADOW.offsetX),
  offsetY: clampShadowOffset(value?.offsetY ?? DEFAULT_DROP_SHADOW.offsetY),
  blur: clampShadowBlur(value?.blur ?? DEFAULT_DROP_SHADOW.blur),
  spread: clampShadowSpread(value?.spread ?? DEFAULT_DROP_SHADOW.spread),
  color: value?.color?.trim() || DEFAULT_DROP_SHADOW.color,
})

export const stripButtonUrlProtocol = (value?: string | null): string => {
  const trimmed = value?.trim() || ''
  if (!trimmed) {
    return ''
  }

  return trimmed
    .replace(/^https?:\/\//i, '')
    .replace(/^\/\//, '')
}

export const ensureHttpsButtonUrl = (value?: string | null): string | undefined => {
  const normalized = stripButtonUrlProtocol(value)
  if (!normalized) {
    return undefined
  }

  return `https://${normalized}`
}

export const isValidButtonUrl = (value?: string | null): boolean => {
  const normalized = stripButtonUrlProtocol(value)
  if (!normalized || /\s/.test(normalized)) {
    return false
  }

  try {
    const parsed = new URL(`https://${normalized}`)
    return Boolean(parsed.hostname)
  } catch {
    return false
  }
}

export const normalizeStyledButtonConfig = (
  value: StyledButtonConfig | null | undefined,
  fallback: StyledButtonConfig,
): StyledButtonConfig => {
  const normalizedTarget = value?.target === '_self' ? '_self' : '_blank'
  const normalizedVariant = value?.styleVariant ?? fallback.styleVariant ?? DEFAULT_VARIANT
  return {
    attributeId: value?.attributeId ?? fallback.attributeId ?? DEFAULT_TARGET,
    label: fallback.label,
    url: stripButtonUrlProtocol(value?.url) || undefined,
    target: normalizedTarget,
    textColor: value?.textColor?.trim() || undefined,
    backgroundColor: value?.backgroundColor?.trim() || undefined,
    borderColor: value?.borderColor?.trim() || undefined,
    borderRadius: clampButtonRadius(value?.borderRadius ?? fallback.borderRadius),
    blurPx: clampBlur(value?.blurPx ?? fallback.blurPx),
    styleVariant: normalizedVariant,
    dropShadow: normalizeDropShadow(value?.dropShadow ?? fallback.dropShadow),
  }
}

const buildDropShadowCSS = (shadow?: DropShadowConfig): string => {
  if (!shadow?.enabled) return 'none'
  const { offsetX, offsetY, blur, spread, color } = shadow
  return `${offsetX}px ${offsetY}px ${blur}px ${spread}px ${color}`
}

const buildOutlineRingShadowCSS = (shadow?: DropShadowConfig): string => {
  if (!shadow?.enabled) return 'none'

  const outerShadow = buildDropShadowCSS(shadow)
  const innerBlur = Math.max(6, Math.round(shadow.blur * 0.85))
  const innerSpread = Math.max(0, shadow.spread)
  const innerShadow = `inset 0 0 ${innerBlur}px ${innerSpread}px ${shadow.color}`

  return `${outerShadow}, ${innerShadow}`
}

const withAlpha = (color: string, alpha: number): string => {
  const normalizedAlpha = Math.min(1, Math.max(0, alpha))
  const trimmed = color.trim()

  const hex = trimmed.replace('#', '')
  if (/^[0-9a-fA-F]{3}$/.test(hex) || /^[0-9a-fA-F]{6}$/.test(hex)) {
    const expanded = hex.length === 3
      ? hex.split('').map((char) => `${char}${char}`).join('')
      : hex
    const r = parseInt(expanded.slice(0, 2), 16)
    const g = parseInt(expanded.slice(2, 4), 16)
    const b = parseInt(expanded.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${normalizedAlpha})`
  }

  const rgbMatch = trimmed.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)$/i,
  )
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch
    return `rgba(${r}, ${g}, ${b}, ${normalizedAlpha})`
  }

  return color
}

const normalizeColorToken = (value?: string | null): string => value?.trim().toUpperCase() || ''

export const buildButtonStyleFallbacks = (
  colors: Pick<ScreenBrandColors, 'secondary' | 'buttonText'>,
): ButtonStyleFallbacks => ({
  backgroundColor: colors.secondary,
  textColor: colors.buttonText,
  borderColor: colors.secondary,
})

export const resolveButtonStyleColors = (
  value: StyledButtonConfig | null | undefined,
  fallbackConfig: StyledButtonConfig,
  fallbacks: ButtonStyleFallbacks,
): ButtonStyleFallbacks => {
  const config = normalizeStyledButtonConfig(value, fallbackConfig)
  const backgroundColor = config.backgroundColor?.trim() || fallbacks.backgroundColor
  const textColor = config.textColor?.trim() || fallbacks.textColor
  const borderColor = config.borderColor?.trim() || config.backgroundColor?.trim() || fallbacks.borderColor

  return {
    backgroundColor,
    textColor,
    borderColor,
  }
}

export const buildButtonEditorConfig = (
  value: StyledButtonConfig | null | undefined,
  fallbackConfig: StyledButtonConfig,
  fallbacks: ButtonStyleFallbacks,
): StyledButtonConfig => {
  const config = normalizeStyledButtonConfig(value, fallbackConfig)
  const colors = resolveButtonStyleColors(config, fallbackConfig, fallbacks)

  return {
    ...config,
    backgroundColor: colors.backgroundColor,
    textColor: colors.textColor,
    borderColor: colors.borderColor,
  }
}

export const stripSharedButtonColorOverrides = (
  value: StyledButtonConfig | null | undefined,
  fallbackConfig: StyledButtonConfig,
  fallbacks: ButtonStyleFallbacks,
): StyledButtonConfig => {
  const config = normalizeStyledButtonConfig(value, fallbackConfig)
  const sharedBackground = normalizeColorToken(fallbacks.backgroundColor)
  const sharedText = normalizeColorToken(fallbacks.textColor)
  const sharedBorder = normalizeColorToken(fallbacks.borderColor)
  const borderColor = normalizeColorToken(config.borderColor)

  return {
    ...config,
    backgroundColor: undefined,
    textColor: undefined,
    borderColor:
      borderColor && borderColor !== sharedBackground && borderColor !== sharedBorder
        ? config.borderColor?.trim()
        : undefined,
  }
}

export const syncButtonColorOverridesWithFallbackChange = (
  value: StyledButtonConfig | null | undefined,
  fallbackConfig: StyledButtonConfig,
  previousFallbacks: ButtonStyleFallbacks,
  nextFallbacks: ButtonStyleFallbacks,
): StyledButtonConfig => {
  const config = normalizeStyledButtonConfig(value, fallbackConfig)
  const backgroundColor = normalizeColorToken(config.backgroundColor)
  const textColor = normalizeColorToken(config.textColor)
  const borderColor = normalizeColorToken(config.borderColor)

  const previousBackground = normalizeColorToken(previousFallbacks.backgroundColor)
  const previousText = normalizeColorToken(previousFallbacks.textColor)
  const previousBorder = normalizeColorToken(previousFallbacks.borderColor)

  const nextBackground = normalizeColorToken(nextFallbacks.backgroundColor)
  const nextText = normalizeColorToken(nextFallbacks.textColor)
  const nextBorder = normalizeColorToken(nextFallbacks.borderColor)

  return {
    ...config,
    backgroundColor:
      backgroundColor &&
      backgroundColor !== previousBackground &&
      backgroundColor !== nextBackground
        ? config.backgroundColor?.trim()
        : undefined,
    textColor:
      textColor &&
      textColor !== previousText &&
      textColor !== nextText
        ? config.textColor?.trim()
        : undefined,
    borderColor:
      borderColor &&
      borderColor !== previousBackground &&
      borderColor !== previousBorder &&
      borderColor !== nextBackground &&
      borderColor !== nextBorder
        ? config.borderColor?.trim()
        : undefined,
  }
}

export const buildButtonPreviewStyle = (
  value: StyledButtonConfig | null | undefined,
  fallbacks: ButtonStyleFallbacks,
  defaultConfig: StyledButtonConfig = DEFAULT_BUTTON_CONFIGS.prize,
): CSSProperties => {
  const config = normalizeStyledButtonConfig(value, defaultConfig)
  const backgroundColor = config.backgroundColor || fallbacks.backgroundColor
  const textColor = config.textColor || fallbacks.textColor
  const borderColor = config.borderColor || backgroundColor || fallbacks.borderColor
  const borderRadius = `${clampButtonRadius(config.borderRadius)}px`
  const blurPx = clampBlur(config.blurPx)
  const styleVariant = config.styleVariant ?? DEFAULT_VARIANT
  const boxShadow = buildDropShadowCSS(config.dropShadow)

  if (styleVariant === 'frosted') {
    const frostedBackground = withAlpha(backgroundColor, 0.5)
    const frostedBorder = withAlpha(borderColor, 0.72)
    const frostedShadow = boxShadow === 'none'
      ? 'inset 0 0 0 1px rgba(255, 255, 255, 0.14)'
      : `inset 0 0 0 1px rgba(255, 255, 255, 0.14), ${boxShadow}`

    return {
      color: textColor,
      border: `1px solid ${frostedBorder}`,
      borderColor: frostedBorder,
      borderRadius,
      background: frostedBackground,
      backgroundColor: frostedBackground,
      backdropFilter: `blur(${blurPx}px)`,
      WebkitBackdropFilter: `blur(${blurPx}px)`,
      boxShadow: frostedShadow,
    }
  }

  if (styleVariant === 'outline') {
    return {
      color: textColor,
      border: `2px solid ${borderColor}`,
      borderColor,
      borderRadius,
      background: 'transparent',
      backgroundColor: 'transparent',
      boxShadow: buildOutlineRingShadowCSS(config.dropShadow),
    }
  }

  return {
    color: textColor,
    border: `1px solid ${borderColor}`,
    borderColor,
    borderRadius,
    background: backgroundColor,
    backgroundColor,
    boxShadow,
  }
}
